import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_WORKSPACE_CONFIG } from '../config/claim-workspace.config';

export type JobAction = 'details' | 'cure' | 'decide' | 'resolve';

/** The focused dialog each action opens, relative to the job. */
export const JOB_ACTION_ROUTES: Record<JobAction, string> = {
  details: 'manage-details',
  cure: 'manage-cure',
  decide: 'manage-adjudication',
  resolve: 'manage-resolution',
};

export interface JobActionState {
  /** Hidden when the user can never do it here. */
  visible: boolean;
  enabled: boolean;
  /** Why it's off, shown as a tooltip. */
  reason: string | null;
}

export interface JobPermissions {
  manageCoverage: boolean;
  manageCure: boolean;
  adjudicate: boolean;
  manageResolution: boolean;
}

const reasons = CLAIM_WORKSPACE_CONFIG.reasons;

const off = (reason: string | null, visible = true): JobActionState => ({ visible, enabled: false, reason });
const on: JobActionState = { visible: true, enabled: true, reason: null };

/** Job actions for the workspace; same rules as the items table, and the API still decides. */
export function getJobActions(
  claim: Claim.Model,
  item: Claim.ClaimItem.Model,
  permissions: JobPermissions
): Record<JobAction, JobActionState> {
  // An authorized job is read-only, like a closed claim; jobs added after a payment stay editable.
  const isEditable = claim.status === Claim.Status.OPEN && !item.authorizationId;
  const Status = Claim.ClaimItem.Status;

  const blocked = (allowed: boolean): JobActionState | null => {
    if (!allowed) return off(reasons.noPermission, false);
    if (item.deleted) return off(reasons.removed);
    if (!isEditable) return off(reasons.notEditable);
    return null;
  };

  const details = blocked(permissions.manageCoverage) ?? (item.status !== Status.DRAFT ? off(reasons.decided) : on);

  const cure = blocked(permissions.manageCure) ?? on;

  const decide =
    blocked(permissions.adjudicate) ??
    (item.status !== Status.DRAFT
      ? off(reasons.decided)
      : !Claim.ClaimItem.readyForAdjudication(claim, item)
        ? off(reasons.notReady)
        : on);

  const resolve =
    blocked(permissions.manageResolution) ??
    (item.status === Status.REJECTED
      ? off(reasons.rejected)
      : item.status !== Status.APPROVED || !item.adjudicationReason
        ? off(reasons.notDecided)
        : !item.cure?.title || !item.cure.description || typeof item.cure.estimatedCost !== 'number'
          ? off(reasons.noCure)
          : on);

  return { details, cure, decide, resolve };
}
